import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import BrandLogo from '../brand/BrandLogo';
import LanguageSwitcher from '../nav/LanguageSwitcher';
import { scrollToSection, setHeaderOffsetCssVar } from './scrollToSection';
import styles from './ComingSoonHeader.module.scss';

export function ComingSoonHeader() {
  const { t } = useTranslation();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef(null);
  const navRef = useRef(null);
  const pathname = typeof location?.pathname === 'string' ? location.pathname : '/';
  const isRootRoute = pathname === '/' || pathname === '';

  const navItems = [
    { targetId: 'what-we-build', label: t('comingSoon.nav.product') },
    { targetId: 'for-who', label: t('comingSoon.nav.who') },
  ];

  const resolveSectionHref = useCallback(
    (targetId) => (isRootRoute ? `#${targetId}` : `/#${targetId}`),
    [isRootRoute],
  );

  const onSectionLinkClick = useCallback(
    (event, targetId) => {
      if (
        event.defaultPrevented ||
        (typeof event.button === 'number' && event.button !== 0) ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.shiftKey
      ) {
        return;
      }

      if (!isRootRoute) {
        setMenuOpen(false);
        return;
      }

      const didScroll = scrollToSection(targetId);
      if (!didScroll) {
        setMenuOpen(false);
        return;
      }

      event.preventDefault();
      setMenuOpen(false);
    },
    [isRootRoute],
  );

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const onPointerDown = (event) => {
      if (!navRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    const onEscape = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onEscape);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onEscape);
    };
  }, [menuOpen]);

  useEffect(() => {
    const headerElement = headerRef.current;
    if (!headerElement) {
      return undefined;
    }

    const syncHeaderOffsetVar = () => {
      setHeaderOffsetCssVar(headerElement);
    };

    syncHeaderOffsetVar();

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        syncHeaderOffsetVar();
      });
      resizeObserver.observe(headerElement);
    }

    window.addEventListener('resize', syncHeaderOffsetVar);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncHeaderOffsetVar);
      document.documentElement.style.removeProperty('--header-offset');
    };
  }, []);

  return (
    <header className={styles.wrapper}>
      <div className="pageContainer">
        <div ref={headerRef} className={styles.navPill} data-site-header="true">
          <nav
            ref={navRef}
            className={`surfaceInner ${styles.navInner}`}
            aria-label={t('comingSoon.nav.ariaPrimary')}
          >
            <div className={styles.leftRail}>
              <BrandLogo className={styles.logo} variant="site" href="/" size="nav" />
              <ul className={styles.linkList}>
                {navItems.map((item) => (
                  <li key={item.targetId}>
                    <a
                      href={resolveSectionHref(item.targetId)}
                      onClick={(event) => onSectionLinkClick(event, item.targetId)}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className={styles.menuToggle}
                aria-expanded={menuOpen}
                aria-controls="coming-soon-nav-menu"
                onClick={() => setMenuOpen((current) => !current)}
              >
                <span aria-hidden="true" className={styles.menuIcon}>
                  <span />
                  <span />
                  <span />
                </span>
                <span className="srOnly">{t('comingSoon.nav.ariaPrimary')}</span>
              </button>
            </div>
            <div className={styles.rightRail}>
              <LanguageSwitcher className={styles.language} triggerStyle="globe" />
              <a
                className={styles.waitlistAction}
                href={resolveSectionHref('waitlist')}
                onClick={(event) => onSectionLinkClick(event, 'waitlist')}
              >
                {t('comingSoon.nav.joinWaitlist')}
              </a>
            </div>
            <div
              id="coming-soon-nav-menu"
              className={`${styles.mobileMenu} ${menuOpen ? styles.mobileMenuOpen : ''}`}
            >
              <ul>
                {navItems.map((item) => (
                  <li key={item.targetId}>
                    <a
                      href={resolveSectionHref(item.targetId)}
                      onClick={(event) => onSectionLinkClick(event, item.targetId)}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}
